import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
} from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import {
    questionnairesApi,
    assessmentsApi,
    crosswalkApi,
    questionChatApi,
} from "../services/api";
import Swal from "sweetalert2";

// ─── Helpers ───────────────────────────────────────────────────────────────

// Normalize the crosswalk rationale field, which the backend may return as:
//   - a plain string (legacy / local fallback), or
//   - a structured object { text, details: { sourceEvidence, targetNeed,
//     controlAlignment, orgRelevance, caveats } }.
// The downstream UI parses a labeled string, so flatten the object into that form.
const normalizeRationale = (r) => {
    if (r == null) return "";
    if (typeof r === "string") return r;
    if (typeof r !== "object") return String(r);
    const parts = [];
    const text = r.rationale || r.text;
    if (text && typeof text === "string") parts.push(`Rationale: ${text}`);
    const d = r.details;
    if (d && typeof d === "object") {
        const sourceEvidence = d.source_evidence || d.sourceEvidence;
        const targetNeed = d.target_need || d.targetNeed;
        const controlAlignment = d.control_alignment || d.controlAlignment;
        const orgRelevance = d.org_relevance || d.orgRelevance;
        const { caveats } = d;
        if (sourceEvidence) parts.push(`Source evidence: ${sourceEvidence}`);
        if (targetNeed) parts.push(`Target need: ${targetNeed}`);
        if (controlAlignment)
            parts.push(`Control objective alignment: ${controlAlignment}`);
        if (orgRelevance) parts.push(`Mapping basis: ${orgRelevance}`);
        if (caveats) parts.push(`Caveats: ${caveats}`);
    }
    return parts.length ? parts.join(" ") : text || "";
};

const normalizeEvidenceDocuments = (proposal) => {
    const enrichments = [
        proposal?.documentEnrichment,
        proposal?.document_enrichment,
        ...(proposal?.documentEnrichments || []),
        ...(proposal?.document_enrichments || []),
    ].filter(Boolean);

    const documents = [
        ...(proposal?.evidenceDocuments || []),
        ...(proposal?.evidence_documents || []),
        ...enrichments.flatMap((e) => e.evidence || []),
    ];

    const seen = new Set();
    return documents
        .map((d) => ({
            chunk_id: d.chunkId || d.chunk_id || null,
            doc_title:
                d.docTitle || d.doc_title || d.fileName || d.file_name || null,
            blob_url: d.blobUrl || d.blob_url || null,
            ref_doc_id: d.refDocId || d.ref_doc_id || null,
            chunk_ordinal: d.chunkOrdinal || d.chunk_ordinal || null,
            section_title: d.sectionTitle || d.section_title || null,
            snippet: d.snippet || null,
            retrieval_score: d.retrievalScore ?? d.retrieval_score ?? null,
        }))
        .filter((d) => d.doc_title || d.blob_url || d.snippet)
        .filter((d) => {
            const key =
                d.chunk_id ||
                `${d.blob_url || d.doc_title || "doc"}:${d.snippet || d.chunk_ordinal || ""}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

const deriveStatus = (qnr) => {
    if (qnr.answeredCount >= qnr.questionCount && qnr.questionCount > 0)
        return "completed";
    if (qnr.answeredCount > 0) return "processing";
    return "pending";
};

const confidenceScoreToLabel = (score) => {
    if (!score) return null;
    const map = {
        1: "Very Low",
        2: "Low",
        3: "Moderate",
        4: "High",
        5: "Very High",
    };
    return map[score] || "Moderate";
};

const confidenceLabelToScore = (label) => {
    if (!label) return null;
    const map = { "Very Low": 1, Low: 2, Moderate: 3, High: 4, "Very High": 5 };
    return map[label] || null;
};

// ─── LOCAL CROSSWALK MATCHING (fallback when Agentic API returns 0 proposals) ───

const STOP_WORDS = new Set([
    "the",
    "a",
    "an",
    "is",
    "are",
    "was",
    "were",
    "be",
    "been",
    "being",
    "have",
    "has",
    "had",
    "do",
    "does",
    "did",
    "will",
    "would",
    "could",
    "should",
    "may",
    "might",
    "must",
    "shall",
    "can",
    "need",
    "dare",
    "ought",
    "used",
    "to",
    "of",
    "in",
    "for",
    "on",
    "with",
    "at",
    "by",
    "from",
    "as",
    "into",
    "through",
    "during",
    "before",
    "after",
    "above",
    "below",
    "between",
    "and",
    "but",
    "or",
    "nor",
    "so",
    "yet",
    "both",
    "either",
    "neither",
    "not",
    "only",
    "own",
    "same",
    "than",
    "too",
    "very",
    "just",
    "also",
    "how",
    "what",
    "which",
    "who",
    "whom",
    "this",
    "that",
    "these",
    "those",
    "its",
    "it",
    "they",
    "their",
    "them",
    "we",
    "our",
    "us",
    "you",
    "your",
    "he",
    "she",
    "him",
    "her",
    "his",
    "hers",
]);

const KEY_PHRASES = [
    "access control",
    "authorized users",
    "security awareness",
    "risk assessment",
    "incident response",
    "security incidents",
    "physical security",
    "security plan",
    "supply chain",
    "personally identifiable",
    "pii",
    "system flaws",
    "system maintenance",
    "identity verification",
    "log and monitor",
    "monitoring",
    "training",
    "personnel",
    "confidentiality",
    "integrity",
    "transmitted information",
    "system disruptions",
    "backup",
    "recovery",
    "security requirements",
    "security program",
    "media protection",
];

const extractKeywords = (text) => {
    if (!text) return new Set();
    const normalized = text.toLowerCase();
    const words = normalized.match(/\b[a-z]{3,}\b/g) || [];
    const keywords = words.filter((w) => !STOP_WORDS.has(w));

    // Add key phrases found in text
    KEY_PHRASES.forEach((phrase) => {
        if (normalized.includes(phrase)) {
            keywords.push(phrase.replace(/\s+/g, "_"));
        }
    });

    return new Set(keywords);
};

const jaccardSimilarity = (set1, set2) => {
    if (set1.size === 0 || set2.size === 0) return 0;
    const intersection = new Set([...set1].filter((x) => set2.has(x)));
    const union = new Set([...set1, ...set2]);
    return intersection.size / union.size;
};

const generateLocalProposals = (
    unansweredQuestions,
    sourceAnswers,
    minConfidence = 0.25,
) => {
    if (!unansweredQuestions?.length || !sourceAnswers?.length) return [];

    const proposals = [];

    for (const targetQ of unansweredQuestions) {
        const targetKeywords = extractKeywords(targetQ.questionText);
        let bestMatch = null;
        let bestScore = 0;

        for (const sourceA of sourceAnswers) {
            const sourceKeywords = extractKeywords(sourceA.questionText);
            const similarity = jaccardSimilarity(
                targetKeywords,
                sourceKeywords,
            );

            if (similarity > bestScore && similarity >= minConfidence) {
                bestScore = similarity;
                bestMatch = sourceA;
            }
        }

        if (bestMatch) {
            proposals.push({
                target_question_id: targetQ.questionId,
                source_answer_id: bestMatch.answerId,
                source_question_id: bestMatch.questionId,
                source_answer_text: bestMatch.answerText,
                confidence: bestScore,
                rationale: `Local keyword matching (${Math.round(bestScore * 100)}% similarity)`,
                scores: {
                    confidence: bestScore,
                    bm25: 0,
                },
                option_alignments: [],
            });
        }
    }

    console.log(
        `LOCAL MATCHING: Generated ${proposals.length} proposals from ${unansweredQuestions.length} questions`,
    );
    return proposals;
};

// Map raw questions + answers into the UI question shape
const mapQuestions = (questionsList, answersList) => {
    const answersLookup = {};
    (answersList || []).forEach((a) => {
        const existing = answersLookup[a.questionId];
        // Prefer reviewer answer over crosswalk so reviewer-typed notes are never overwritten
        if (!existing || a.level === "reviewer") {
            answersLookup[a.questionId] = a;
        }
    });

    return (questionsList || []).map((q) => {
        const answer = answersLookup[q.questionId];
        const options = (q.answerOptions || []).map((o) => o.value);

        let selectedOption = null;
        if (answer?.selectedOption) {
            const idx = options.findIndex(
                (o) => o.toLowerCase() === answer.selectedOption.toLowerCase(),
            );
            if (idx >= 0) selectedOption = idx;
        }

        return {
            id: q.questionId,
            text: q.questionText,
            options: options.length > 0 ? options : ["Yes", "No", "NA"],
            answerOptions: q.answerOptions || [], // Preserve full answer options with IDs
            selectedOption,
            confidence: confidenceScoreToLabel(answer?.confidenceScore),
            pendingAnswer: null,
            pendingReason: "",
            pendingConfidence: null,
            reviewStatus: answer?.reviewStatus || null,
            selected: false,
            showExplanation: false,
            showUploadPanel: false,
            saving: false,
            rationale: [],
            crossWalkTags: q.controlIdentifier ? [q.controlIdentifier] : [],
            // Load previously saved reviewer notes so they survive a reload / tab
            // switch. Persisted in the answer_reasons column (see saveReviewerNotes).
            reviewerNotes: answer?.answerReasons || "",
            detailedReview: {
                description: q.instructions || "",
                provenance: "",
                crossWalk: q.controlIdentifier || "",
                audit: "",
            },
            answerId: answer?.answerId || null,
            additionalInfo: answer?.additionalInfo || null,
            additionalInfoUrl: answer?.additionalinfoUrl || null,
            refDocCount: answer?.refDocCount ?? 0,
        };
    });
};

// ─── Context ───────────────────────────────────────────────────────────────

const ReviewerContext = createContext(null);

export const useReviewerContext = () => {
    const context = useContext(ReviewerContext);
    if (!context) {
        throw new Error(
            "useReviewerContext must be used within ReviewerProvider",
        );
    }
    return context;
};

// ─── Provider ──────────────────────────────────────────────────────────────

export const ReviewerProvider = ({ children }) => {
    const { assessmentId } = useParams();
    const { user } = useAuth();

    // ═══════════════════════════════════════════════════════════════════════════
    // ─── STATES ────────────────────────────────────────────────────────────────
    // ═══════════════════════════════════════════════════════════════════════════

    // Questionnaire bar state
    const [questionnaires, setQuestionnaires] = useState([]);
    const [activeQnr, setActiveQnr] = useState(null);

    // Loading & error states
    const [loading, setLoading] = useState(true);
    const [crosswalkLoading, setCrosswalkLoading] = useState(false);
    const [proposalStatus, setProposalStatus] = useState(null);
    const [error, setError] = useState(null);
    const [questionsLoading, setQuestionsLoading] = useState(false);

    // Filter & search
    const [activeFilter, setActiveFilter] = useState("All");
    const [searchTerm, setSearchTerm] = useState("");

    // Query (per-question chat) filter: "all" | "open" | "resolved" | "any".
    const [queryFilter, setQueryFilter] = useState("all");
    // questionId → 1 (open) | 2 (resolved). Questions with no thread are absent.
    const [chatStatusMap, setChatStatusMap] = useState({});

    // Cross-walk answers
    const [crossWalkAnswers, setCrossWalkAnswers] = useState([
        // {
        //   "answer_id": "",
        //   "question_id": 3333,
        //   "question_text": "",
        //   "questionnaire_id": 113,
        //   "question_type": "Single",
        //   "assessment_id": 227,
        //   "account_id": 3,
        //   "answer_text": "green"
        // },
        // {
        //   "answer_id": "",
        //   "question_id": 3334,
        //   "question_text": "",
        //   "questionnaire_id": 113,
        //   "question_type": "Single",
        //   "assessment_id": 227,
        //   "account_id": 3,
        //   "answer_text": "abc"
        // }
    ]);

    // Proposal
    const [proposal, setProposal] = useState([
        // {
        //   "target_question_id": 3317,
        //   "source_answer_id": 747,
        //   "confidence": 1,
        //   "rationale": "Semantic match with confidence 1.00",
        //   "scores": {
        //     "confidence": 1,
        //     "bm25": 10.320474245041813
        //   },
        //   "option_alignments": []
        // },
        // {
        //   "target_question_id": 3333,
        //   "source_answer_id": 748,
        //   "confidence": 1,
        //   "rationale": "Semantic match with confidence 1.00",
        //   "scores": {
        //     "confidence": 1,
        //     "bm25": 17.14139907636851
        //   },
        //   "option_alignments": []
        // },
        // {
        //   "target_question_id": 3419,
        //   "source_answer_id": 749,
        //   "confidence": 1,
        //   "rationale": "Semantic match with confidence 1.00",
        //   "scores": {
        //     "confidence": 1,
        //     "bm25": 5.070586328359253
        //   },
        //   "option_alignments": []
        // },
        // {
        //   "target_question_id": 3421,
        //   "source_answer_id": 750,
        //   "confidence": 1,
        //   "rationale": "Semantic match with confidence 1.00",
        //   "scores": {
        //     "confidence": 1,
        //     "bm25": 7.009414517467317
        //   },
        //   "option_alignments": []
        // },
        // {
        //   "target_question_id": 3422,
        //   "source_answer_id": 751,
        //   "confidence": 1,
        //   "rationale": "Semantic match with confidence 1.00",
        //   "scores": {
        //     "confidence": 1,
        //     "bm25": 7.820129262877643
        //   },
        //   "option_alignments": []
        // },
        // {
        //   "target_question_id": 3423,
        //   "source_answer_id": 752,
        //   "confidence": 1,
        //   "rationale": "Semantic match with confidence 1.00",
        //   "scores": {
        //     "confidence": 1,
        //     "bm25": 9.566775999571869
        //   },
        //   "option_alignments": []
        // }
    ]);

    // Source answer
    const [sourceAnswer, setSourceAnswer] = useState([
        // {
        //   "answer_id": 747,
        //   "question_id": 3333,
        //   "question_text": "Does the service provided by the business entity include support for a critical component of the organization's product or services?",
        //   "questionnaire_id": 33,
        //   "question_type": "Single",
        //   "assessment_id": 35,
        //   "account_id": 3,
        //   "answer_text": "Yes. It supports the core business of organization."
        // },
        // {
        //   "answer_id": 748,
        //   "question_id": 3418,
        //   "question_text": "Does the types of data the organization interacts with include any Personally Identifiable Information (PII), Commercially Sensitive Information (CSI) or Payment Card Information (PCI)?",
        //   "questionnaire_id": 33,
        //   "question_type": "Single",
        //   "assessment_id": 35,
        //   "account_id": 3,
        //   "answer_text": "Personally Identifiable Information (PII)"
        // },
        // {
        //   "answer_id": 749,
        //   "question_id": 3419,
        //   "question_text": "Is this service provided by only this Business Entity?",
        //   "questionnaire_id": 33,
        //   "question_type": "Single",
        //   "assessment_id": 35,
        //   "account_id": 3,
        //   "answer_text": "No"
        // },
        // {
        //   "answer_id": 750,
        //   "question_id": 3421,
        //   "question_text": "What are the methods of data transfer with the Business Entity?",
        //   "questionnaire_id": 33,
        //   "question_type": "Multiple",
        //   "assessment_id": 35,
        //   "account_id": 3,
        //   "answer_text": "Plaintext email; FTP; Secure FTP; HTTPS"
        // },
        // {
        //   "answer_id": 751,
        //   "question_id": 3422,
        //   "question_text": "Does the organization have a formal contract in place?",
        //   "questionnaire_id": 33,
        //   "question_type": "Single",
        //   "assessment_id": 35,
        //   "account_id": 3,
        //   "answer_text": "Yes. Pending Review"
        // },
        // {
        //   "answer_id": 752,
        //   "question_id": 3423,
        //   "question_text": "Where are the services delivered by the third party hosted?",
        //   "questionnaire_id": 33,
        //   "question_type": "Single",
        //   "assessment_id": 35,
        //   "account_id": 3,
        //   "answer_text": null
        // }
    ]);

    // Questions state — each questionnaire has its own independent question list
    const [questionsMap, setQuestionsMap] = useState({});
    const [selectAll, setSelectAll] = useState(false);

    // Two-way acceptance state
    const [acceptanceStatus, setAcceptanceStatus] = useState({
        reviewerAccepted: false,
        userAccepted: false,
        userRejected: false,
        bothAccepted: false,
    });
    const [reviewerAccepting, setReviewerAccepting] = useState(false);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);

    // ═══════════════════════════════════════════════════════════════════════════
    // ─── COMPUTED / DERIVED VALUES ─────────────────────────────────────────────
    // ═══════════════════════════════════════════════════════════════════════════

    const questions = questionsMap[activeQnr] || [];

    // Query filter: "all" = every question, "open"/"resolved" = threads in that
    // state, "any" = questions that have a thread at all (status 1 or 2).
    const filteredQuestions = questions.filter((q) => {
        if (queryFilter === "all") return true;
        const status = chatStatusMap[q.id];
        if (queryFilter === "open") return status === 1;
        if (queryFilter === "resolved") return status === 2;
        return status === 1 || status === 2;
    });

    const questionsPerPage = 5;
    const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);
    const paginatedQuestions = filteredQuestions.slice(
        (currentPage - 1) * questionsPerPage,
        currentPage * questionsPerPage,
    );
    const allReviewed = questions.every((q) => q.reviewStatus !== null);
    const anySelected = questions.some((q) => q.selected);
    const anyPending = questions.some((q) => q.reviewStatus === null);
    const filteredQnrs = questionnaires.filter((qnr) => {
        if (activeFilter === "All") return true;
        return qnr.status === activeFilter.toLowerCase();
    });

    // ═══════════════════════════════════════════════════════════════════════════
    // ─── EFFECTS ───────────────────────────────────────────────────────────────
    // ═══════════════════════════════════════════════════════════════════════════

    // Load the per-question query statuses for this assessment. Called on mount
    // and again whenever a chat panel closes, so the filter and the "View Query"
    // button reflect threads created/accepted during the session.
    const refreshChatStatuses = useCallback(async () => {
        if (!assessmentId) return;
        try {
            const rows = await questionChatApi.getThreadSummary(
                parseInt(assessmentId, 10),
            );
            const map = {};
            (Array.isArray(rows) ? rows : []).forEach((r) => {
                map[r.questionId] = r.status;
            });
            setChatStatusMap(map);
        } catch (err) {
            console.error("Failed to load query statuses", err);
        }
    }, [assessmentId]);

    useEffect(() => {
        refreshChatStatuses();
    }, [refreshChatStatuses]);

    // A narrower filter can leave the current page out of range.
    useEffect(() => {
        setCurrentPage(1);
    }, [queryFilter]);

    // Fetch questionnaires on mount
    useEffect(() => {
        const fetchQuestionnaires = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await questionnairesApi.getAll(
                    1,
                    100,
                    null,
                    null,
                    assessmentId || null,
                );
                const items =
                    res.items || res.data || (Array.isArray(res) ? res : []);

                const mapped = items.map((qnr) => ({
                    id: String(qnr.questionnaireId),
                    name: qnr.questionnaireName,
                    status: deriveStatus(qnr),
                    crosswalkJobId: qnr.crosswalkJobId,
                    progress: {
                        answered: qnr.answeredCount || 0,
                        reviewed: 0,
                        total: qnr.questionCount || 0,
                    },
                    updatedAt: qnr.lastUpdated
                        ? new Date(qnr.lastUpdated).toLocaleDateString(
                              "en-US",
                              {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                              },
                          )
                        : null,
                }));

                setQuestionnaires(mapped);
                if (mapped.length > 0) {
                    setActiveQnr(mapped[0].id);
                }

                // Preload questions/answers for each questionnaire
                mapped.forEach((m) => {
                    (async () => {
                        try {
                            const [questionsRes, answersRes] =
                                await Promise.all([
                                    questionnairesApi
                                        .getQuestions(m.id)
                                        .catch(() => []),
                                    assessmentId
                                        ? questionnairesApi
                                              .getAnswers(m.id, assessmentId)
                                              .catch(() => [])
                                        : Promise.resolve([]),
                                ]);
                            const questionsList = Array.isArray(questionsRes)
                                ? questionsRes
                                : questionsRes.items || [];
                            const answersList = Array.isArray(answersRes)
                                ? answersRes
                                : answersRes.items || [];
                            const mappedQs = mapQuestions(
                                questionsList,
                                answersList,
                            );
                            setQuestionsMap((prev) => ({
                                ...prev,
                                [m.id]: mappedQs,
                            }));
                            const answeredCount = mappedQs.filter(
                                (qq) => qq.selectedOption !== null,
                            ).length;
                            setQuestionnaires((prev) =>
                                prev.map((qnr) =>
                                    qnr.id === m.id
                                        ? {
                                              ...qnr,
                                              progress: {
                                                  ...qnr.progress,
                                                  answered: answeredCount,
                                                  total: mappedQs.length,
                                              },
                                          }
                                        : qnr,
                                ),
                            );
                        } catch (e) {
                            // ignore background preload failures
                        }
                    })();
                });
            } catch (err) {
                console.error("Failed to load questionnaires", err);
                setError(err.message || "Failed to load questionnaires");
            } finally {
                setLoading(false);
            }
        };

        fetchQuestionnaires();
    }, [assessmentId]);

    // Fetch questions when active questionnaire changes
    useEffect(() => {
        if (activeQnr && !questionsMap[activeQnr]) {
            fetchQuestionsForQnr(activeQnr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeQnr]);

    // Fetch crosswalk data separately (only when activeQnr changes, not on questionsMap updates)
    useEffect(() => {
        if (activeQnr) {
            fetchCrosswalkData(activeQnr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeQnr]);

    // Keep questionnaire cards in sync with per-question data
    useEffect(() => {
        if (!questionnaires || questionnaires.length === 0) return;
        setQuestionnaires((prev) =>
            prev.map((qnr) => {
                const qs = questionsMap[qnr.id] || [];
                if (qs.length === 0) return qnr;
                const reviewed = qs.filter(
                    (qq) => qq.reviewStatus !== null,
                ).length;
                const answered = qs.filter(
                    (qq) =>
                        qq.selectedOption != null ||
                        (Array.isArray(crossWalkAnswers) &&
                            crossWalkAnswers.some(
                                (cw) => cw.question_id === qq.id,
                            )),
                ).length;
                const total = qs.length;
                return {
                    ...qnr,
                    progress: { ...qnr.progress, reviewed, answered, total },
                };
            }),
        );
    }, [questionsMap, crossWalkAnswers]);

    // Debug effect: Log proposal and sourceAnswer state changes
    // useEffect(() => {
    //   console.log('=== REVIEWER STATE DEBUG ===');
    //   console.log('Current proposal state:', proposal);
    //   console.log('Current sourceAnswer state:', sourceAnswer);
    //   console.log('Current crossWalkAnswers state:', crossWalkAnswers);
    //   console.log('Active questionnaire:', activeQnr);
    //   console.log('Questions for active qnr:', questionsMap[activeQnr]?.length || 0);
    //   console.log('============================');
    // }, [proposal, sourceAnswer, crossWalkAnswers, activeQnr, questionsMap]);

    // ═══════════════════════════════════════════════════════════════════════════
    // ─── FUNCTIONS ─────────────────────────────────────────────────────────────
    // ═══════════════════════════════════════════════════════════════════════════

    const fetchQuestionsForQnr = useCallback(
        async (qnrId) => {
            if (!qnrId) return;
            setQuestionsLoading(true);
            try {
                // Materialize any crosswalk proposals into answers before loading them, so the
                // auto-prefilled selections show up for review. Idempotent and best-effort.
                if (assessmentId) {
                    await crosswalkApi
                        .syncProposals(assessmentId, qnrId)
                        .catch(() => {});
                }

                const [questionsRes, answersRes] = await Promise.all([
                    questionnairesApi.getQuestions(qnrId),
                    assessmentId
                        ? questionnairesApi
                              .getAnswers(qnrId, assessmentId)
                              .catch(() => [])
                        : Promise.resolve([]),
                ]);

                const questionsList = Array.isArray(questionsRes)
                    ? questionsRes
                    : questionsRes.items || [];
                const answersList = Array.isArray(answersRes)
                    ? answersRes
                    : answersRes.items || [];

                const mapped = mapQuestions(questionsList, answersList);
                setQuestionsMap((prev) => ({ ...prev, [qnrId]: mapped }));
            } catch (err) {
                console.error("Failed to load questions", err);
            } finally {
                setQuestionsLoading(false);
            }
        },
        [assessmentId],
    );

    // Fetch crosswalk proposals directly from DB. This is identical for reviewer,
    // user, and admin paths: it does not depend on the upstream Agentic API job
    // cache (which has a short TTL) and works as long as orchestration persisted
    // proposals to the database.
    const fetchCrosswalkData = useCallback(
        async (qnrId) => {
            setCrosswalkLoading(true);
            if (!qnrId || !assessmentId) return;

            try {
                const response = await crosswalkApi.getProposals(
                    assessmentId,
                    qnrId,
                );
                const dbProposals = response?.proposals || [];
                setProposalStatus(response?.status || null);

                if (dbProposals.length === 0) {
                    setProposal([]);
                    setSourceAnswer([]);
                    setCrossWalkAnswers([]);
                    return;
                }

                // Filter and log only proposals with nested rationale structure
                const nestedRationaleProposals = dbProposals.filter(
                    (p) =>
                        p.rationale &&
                        typeof p.rationale === "object" &&
                        p.rationale.details,
                );
                console.log(
                    "Proposals with nested rationale structure:",
                    nestedRationaleProposals,
                );
                console.log(
                    `Found ${nestedRationaleProposals.length} out of ${dbProposals.length} with nested rationale`,
                );

                const formattedProposals = nestedRationaleProposals.map(
                    (p) => ({
                        target_question_id: p.targetQuestionId,
                        source_answer_id: p.sourceAnswerId,
                        source_question_id: p.sourceQuestionId,
                        source_answer_text: p.sourceAnswerText,
                        confidence: p.confidence,
                        rationale: p.rationale?.rationale || "",
                        rationale_details: p.rationale?.details || null,
                        scores: {
                            confidence: p.confidence,
                            bm25: p.scoreBm25 || 0,
                        },
                        option_alignments: (p.optionAlignments || []).map(
                            (o) => ({
                                target_option_id: o.targetOptionId,
                                source_option_text: o.sourceOptionText,
                                alignment_score: o.similarityScore || 0,
                                is_primary: o.isPrimarySuggestion,
                            }),
                        ),
                        document_enrichment:
                            p.documentEnrichment ||
                            p.document_enrichment ||
                            null,
                        document_enrichments:
                            p.documentEnrichments ||
                            p.document_enrichments ||
                            [],
                        // Documents and cited chunks the crosswalk grounded this proposal on.
                        evidence_documents: normalizeEvidenceDocuments(p),
                    }),
                );
                setProposal(formattedProposals);

                // Build sourceAnswer from the unique source answers embedded in proposals.
                // Backend enriches each proposal with source assessment / questionnaire / question
                // context so the Source Mapping panel can render Assessment / Questionnaire /
                // Question / Answer rows.
                const seenIds = new Set();
                const syntheticSourceAnswers = [];
                dbProposals.forEach((p) => {
                    if (p.sourceAnswerId && !seenIds.has(p.sourceAnswerId)) {
                        seenIds.add(p.sourceAnswerId);
                        syntheticSourceAnswers.push({
                            answer_id: p.sourceAnswerId,
                            question_id: p.sourceQuestionId,
                            question_text: p.sourceQuestionText || null,
                            answer_text: p.sourceAnswerText,
                            questionnaire_id: p.sourceQuestionnaireId || null,
                            questionnaire_name:
                                p.sourceQuestionnaireName || null,
                            assessment_id: p.sourceAssessmentId || null,
                            assessment_description:
                                p.sourceAssessmentDescription || null,
                        });
                    }
                });
                setSourceAnswer(syntheticSourceAnswers);
                setCrossWalkAnswers(syntheticSourceAnswers);
            } catch (err) {
                console.error("CROSSWALK ERROR:", err);
            } finally {
                setCrosswalkLoading(false);
            }
        },
        [assessmentId],
    );

    const handleFilterChange = (newFilter) => {
        setActiveFilter(newFilter);
        const filtered = questionnaires.filter((qnr) => {
            if (newFilter === "All") return true;
            return qnr.status === newFilter.toLowerCase();
        });
        if (filtered.length > 0) {
            setActiveQnr(filtered[0].id);
            setSelectAll(false);
            setCurrentPage(1);
        }
    };

    const handleSwitchQnr = async (qnrId) => {
        const currentQuestions = questionsMap[activeQnr] || [];
        const hasUnsaved = currentQuestions.some(
            (q) => q.pendingAnswer !== null || q.pendingConfidence !== null,
        );

        if (hasUnsaved) {
            const result = await Swal.fire({
                title: "Unsaved Changes",
                text: "You have unsaved answer changes. Are you sure you want to switch? Unsaved changes will be lost.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#F47F2E",
                cancelButtonColor: "#919191",
                confirmButtonText: "Yes, switch",
                cancelButtonText: "Cancel",
                background: "#ffffff",
                color: "#53545a",
                iconColor: "#F47F2E",
            });
            if (!result.isConfirmed) return;

            setQuestionsMap((prev) => ({
                ...prev,
                [activeQnr]: currentQuestions.map((q) => ({
                    ...q,
                    pendingAnswer: null,
                    pendingReason: "",
                    pendingConfidence: null,
                })),
            }));
        }

        setActiveQnr(qnrId);
        setSelectAll(false);
        setCurrentPage(1);
    };

    const setQuestions = (updaterOrValue) => {
        setQuestionsMap((prev) => {
            const currentQuestions = prev[activeQnr] || [];
            const newQuestions =
                typeof updaterOrValue === "function"
                    ? updaterOrValue(currentQuestions)
                    : updaterOrValue;
            return { ...prev, [activeQnr]: newQuestions };
        });
    };

    const updateQuestion = (id, updates) => {
        setQuestionsMap((prev) => {
            const currentQuestions = prev[activeQnr] || [];
            return {
                ...prev,
                [activeQnr]: currentQuestions.map((q) =>
                    q.id === id ? { ...q, ...updates } : q,
                ),
            };
        });
    };

    // Resolve the option a reviewer's decision should default to when they have
    // not manually picked one, using the crosswalk proposal's semantic option
    // alignments (Questionnaire Intelligence). Mirrors QuestionCard: pick the
    // primary suggestion (or highest scoring) and require ≥ 50% similarity.
    // Returns the option value string, or null when there is no confident match.
    const matchOptionFromProposal = (questionId, question) => {
        const rec = proposal?.find((r) => r.target_question_id === questionId);
        const alignments = rec?.option_alignments || [];
        if (alignments.length === 0) return null;

        const primary =
            alignments.find((a) => a.is_primary) ||
            [...alignments].sort(
                (a, b) => (b.alignment_score || 0) - (a.alignment_score || 0),
            )[0];

        if (!primary || (primary.alignment_score || 0) < 0.5) return null;

        const matched = question?.answerOptions?.find(
            (o) => o.id === primary.target_option_id,
        );
        return matched?.value || null;
    };

    const handleApprove = async (id) => {
        try {
            // Find the question to get its current/pending selected option
            const question = questions.find((q) => q.id === id);
            const selectedOptionIdx =
                question?.pendingAnswer ?? question?.selectedOption;
            let selectedOption =
                selectedOptionIdx !== null && question?.options
                    ? question.options[selectedOptionIdx]
                    : null;

            // If no selected option, fall back to the crosswalk proposal's
            // semantic option alignment (same logic as QuestionCard).
            if (!selectedOption) {
                selectedOption = matchOptionFromProposal(id, question);
            }

            // Find the option ID if available
            const answerOption = question?.answerOptions?.find(
                (o) => o.value === selectedOption,
            );
            const selectedOptionId = answerOption?.id || null;

            // console.log('APPROVE: questionId=', id, 'selectedOption=', selectedOption, 'selectedOptionId=', selectedOptionId);

            await questionnairesApi.submitReview(activeQnr, {
                questionId: id,
                assessmentId: parseInt(assessmentId, 10),
                reviewStatus: "approved",
                selectedOption: selectedOption,
                selectedOptionId: selectedOptionId,
                confidenceScore: question?.confidence
                    ? [
                          "Very Low",
                          "Low",
                          "Moderate",
                          "High",
                          "Very High",
                      ].indexOf(question.confidence) + 1
                    : 3,
            });
            updateQuestion(id, { reviewStatus: "approved", selected: false });
        } catch (err) {
            console.error("Failed to approve", err);
        }
    };

    const handleReject = async (id) => {
        try {
            // Find the question to get its current/pending selected option (for creating the answer record)
            const question = questions.find((q) => q.id === id);
            const selectedOptionIdx =
                question?.pendingAnswer ?? question?.selectedOption;
            let selectedOption =
                selectedOptionIdx !== null && question?.options
                    ? question.options[selectedOptionIdx]
                    : null;

            // If no selected option, fall back to the crosswalk proposal's
            // semantic option alignment (same logic as QuestionCard).
            if (!selectedOption) {
                selectedOption = matchOptionFromProposal(id, question);
            }

            // console.log('REJECT: questionId=', id, 'selectedOption=', selectedOption);

            await questionnairesApi.submitReview(activeQnr, {
                questionId: id,
                assessmentId: parseInt(assessmentId, 10),
                reviewStatus: "rejected",
                selectedOption: selectedOption,
            });
            updateQuestion(id, { reviewStatus: "rejected", selected: false });
        } catch (err) {
            console.error("Failed to reject", err);
        }
    };

    const handleUndo = async (id) => {
        try {
            await questionnairesApi.submitReview(activeQnr, {
                questionId: id,
                assessmentId: parseInt(assessmentId, 10),
                reviewStatus: null,
            });
            updateQuestion(id, {
                reviewStatus: null,
                selected: false,
                pendingAnswer: null,
                pendingConfidence: null,
                saving: false,
            });
        } catch (err) {
            console.error("Failed to undo", err);
        }
    };

    const handleUndoAll = async () => {
        try {
            await questionnairesApi.submitBulkReview(activeQnr, {
                assessmentId: parseInt(assessmentId, 10),
                items: questions.map((q) => ({
                    questionId: q.id,
                    reviewStatus: null,
                })),
            });
            setQuestions((prev) =>
                prev.map((q) => ({
                    ...q,
                    reviewStatus: null,
                    selected: false,
                    pendingAnswer: null,
                    pendingConfidence: null,
                })),
            );
        } catch (err) {
            console.error("Failed to undo all", err);
        }
    };

    // ── Two-Way Acceptance ────────────────────────────────────────────────────

    const fetchAcceptanceStatus = useCallback(async () => {
        if (!assessmentId || !user?.id) return;
        try {
            const status = await assessmentsApi.getAcceptanceStatus(
                assessmentId,
                user.id,
            );
            setAcceptanceStatus({
                reviewerAccepted: status.reviewerAccepted ?? false,
                userAccepted: status.userAccepted ?? false,
                userRejected: status.userRejected ?? false,
                bothAccepted: status.bothAccepted ?? false,
            });
        } catch (err) {
            console.error("Failed to fetch acceptance status:", err);
        }
    }, [assessmentId, user?.id]);

    useEffect(() => {
        fetchAcceptanceStatus();
    }, [fetchAcceptanceStatus]);

    const handleReviewerAccept = async () => {
        const result = await Swal.fire({
            title: "Accept Assessment?",
            text: "By clicking Accept, you confirm that you have reviewed and approve all answers in this assessment.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#0077b5",
            cancelButtonColor: "#919191",
            confirmButtonText: "Yes, Accept",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        setReviewerAccepting(true);
        try {
            const res = await assessmentsApi.reviewerAccept(
                parseInt(assessmentId, 10),
                user?.email || null,
            );
            await fetchAcceptanceStatus();
            if (res?.emailsSent === false) {
                Swal.fire({
                    title: "Accepted — email not sent",
                    text: "Your acceptance was recorded, but the notification email could not be sent (email service error). Please ask the team to check the email configuration.",
                    icon: "warning",
                    confirmButtonColor: "#0077b5",
                });
            } else {
                Swal.fire({
                    title: "Accepted!",
                    text: acceptanceStatus.userAccepted
                        ? "Both parties have accepted. Acceptance emails with PDF report are being sent."
                        : "Your acceptance has been recorded. The report will be sent once the user also accepts.",
                    icon: "success",
                    confirmButtonColor: "#0077b5",
                });
            }
        } catch (err) {
            console.error("Failed to record reviewer acceptance:", err);
            Swal.fire({
                title: "Error",
                text: "Failed to record your acceptance. Please try again.",
                icon: "error",
                confirmButtonColor: "#0077b5",
            });
        } finally {
            setReviewerAccepting(false);
        }
    };

    const handleApproveAll = async () => {
        try {
            await questionnairesApi.submitBulkReview(activeQnr, {
                assessmentId: parseInt(assessmentId, 10),
                items: questions.map((q) => ({
                    questionId: q.id,
                    reviewStatus: "approved",
                })),
            });
            setQuestions((prev) =>
                prev.map((q) => ({
                    ...q,
                    reviewStatus: "approved",
                    selected: false,
                })),
            );
            setSelectAll(false);
        } catch (err) {
            console.error("Failed to approve all", err);
        }
    };

    const handleRejectAll = async () => {
        try {
            await questionnairesApi.submitBulkReview(activeQnr, {
                assessmentId: parseInt(assessmentId, 10),
                items: questions.map((q) => ({
                    questionId: q.id,
                    reviewStatus: "rejected",
                })),
            });
            setQuestions((prev) =>
                prev.map((q) => ({
                    ...q,
                    reviewStatus: "rejected",
                    selected: false,
                })),
            );
            setSelectAll(false);
        } catch (err) {
            console.error("Failed to reject all", err);
        }
    };

    const handleApproveSelected = async () => {
        const selectedQuestions = questions.filter((q) => q.selected);
        try {
            await questionnairesApi.submitBulkReview(activeQnr, {
                assessmentId: parseInt(assessmentId, 10),
                items: selectedQuestions.map((q) => ({
                    questionId: q.id,
                    reviewStatus: "approved",
                })),
            });
            const selectedIds = new Set(selectedQuestions.map((q) => q.id));
            setQuestions((prev) =>
                prev.map((q) =>
                    selectedIds.has(q.id)
                        ? { ...q, reviewStatus: "approved", selected: false }
                        : q,
                ),
            );
            setSelectAll(false);
        } catch (err) {
            console.error("Failed to approve selected", err);
        }
    };

    const handleRejectSelected = async () => {
        const selectedQuestions = questions.filter((q) => q.selected);
        try {
            await questionnairesApi.submitBulkReview(activeQnr, {
                assessmentId: parseInt(assessmentId, 10),
                items: selectedQuestions.map((q) => ({
                    questionId: q.id,
                    reviewStatus: "rejected",
                })),
            });
            const selectedIds = new Set(selectedQuestions.map((q) => q.id));
            setQuestions((prev) =>
                prev.map((q) =>
                    selectedIds.has(q.id)
                        ? { ...q, reviewStatus: "rejected", selected: false }
                        : q,
                ),
            );
            setSelectAll(false);
        } catch (err) {
            console.error("Failed to reject selected", err);
        }
    };

    const toggleSelectAll = () => {
        const newVal = !selectAll;
        setSelectAll(newVal);
        setQuestionsMap((prev) => {
            const currentQuestions = prev[activeQnr] || [];
            return {
                ...prev,
                [activeQnr]: currentQuestions.map((q) =>
                    q.reviewStatus === null ? { ...q, selected: newVal } : q,
                ),
            };
        });
    };

    const toggleSelect = (id) => {
        setQuestionsMap((prev) => {
            const currentQuestions = prev[activeQnr] || [];
            const updated = currentQuestions.map((q) =>
                q.id === id ? { ...q, selected: !q.selected } : q,
            );
            const allPending = updated.filter((q) => q.reviewStatus === null);
            setSelectAll(
                allPending.length > 0 && allPending.every((q) => q.selected),
            );
            return { ...prev, [activeQnr]: updated };
        });
    };

    const handleOptionChange = (questionId, optionIndex) => {
        const q = questions.find((q) => q.id === questionId);
        if (q) {
            updateQuestion(questionId, {
                pendingAnswer: optionIndex,
                showUploadPanel: true,
            });
        }
    };

    const handleCloseUploadPanel = (questionId) => {
        updateQuestion(questionId, { showUploadPanel: false });
    };

    const handleConfidenceChange = (questionId, level) => {
        updateQuestion(questionId, { pendingConfidence: level });
    };

    const handleSaveAnswer = async (questionId) => {
        const q = questions.find((q) => q.id === questionId);
        if (!q) return;

        const optionIdx =
            q.pendingAnswer !== null ? q.pendingAnswer : q.selectedOption;
        const confidence = q.pendingConfidence || q.confidence;
        if (optionIdx === null) return;

        updateQuestion(questionId, { saving: true });
        try {
            const selectedOptionText = q.options[optionIdx];
            const matchingOption = q.answerOptions?.find(
                (o) => o.value === selectedOptionText,
            );
            await questionnairesApi.submitAnswer(activeQnr, {
                questionId: q.id,
                assessmentId: parseInt(assessmentId, 10),
                questionnaireId: parseInt(activeQnr, 10),
                selectedOption: selectedOptionText,
                selectedOptionId: matchingOption?.id || null,
                confidenceScore: confidenceLabelToScore(confidence),
                // Persist the reviewer notes (not the unused pendingReason) so the
                // Save button actually stores what the reviewer typed.
                answerReasons: q.reviewerNotes || "",
            });
            updateQuestion(questionId, {
                selectedOption: optionIdx,
                confidence: confidence,
                pendingAnswer: null,
                pendingReason: "",
                pendingConfidence: null,
                showUploadPanel: false,
                saving: false,
            });
            setQuestions((current) => {
                const updated = current.map((qq) =>
                    qq.id === questionId
                        ? { ...qq, selectedOption: optionIdx, confidence }
                        : qq,
                );
                const answeredCount = updated.filter(
                    (qq) => qq.selectedOption !== null,
                ).length;
                setQuestionnaires((prev) =>
                    prev.map((qnr) =>
                        qnr.id === activeQnr
                            ? {
                                  ...qnr,
                                  progress: {
                                      ...qnr.progress,
                                      answered: answeredCount,
                                      total: updated.length,
                                  },
                              }
                            : qnr,
                    ),
                );
                return updated;
            });
        } catch (err) {
            console.error("Failed to save answer", err);
            updateQuestion(questionId, { saving: false });
        }
    };
    const saveReviewerNotes = async (questionId) => {
        const q = questions.find((q) => q.id === questionId);
        if (!q) return;
        try {
            await questionnairesApi.submitAnswer(activeQnr, {
                questionId: q.id,
                assessmentId: parseInt(assessmentId, 10),
                questionnaireId: parseInt(activeQnr, 10),
                answerReasons: q.reviewerNotes || "",
            });
        } catch (err) {
            console.error("Failed to save reviewer notes", err);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // ─── CONTEXT VALUE ─────────────────────────────────────────────────────────
    // ═══════════════════════════════════════════════════════════════════════════

    const value = {
        // States
        questionnaires,
        activeQnr,
        loading,
        crosswalkLoading,
        proposalStatus,
        error,
        questionsLoading,
        activeFilter,
        searchTerm,
        crossWalkAnswers,
        proposal,
        sourceAnswer,
        questionsMap,
        selectAll,
        currentPage,
        queryFilter,
        chatStatusMap,

        // Computed values
        questions,
        questionsPerPage,
        totalPages,
        paginatedQuestions,
        filteredQuestions,
        allReviewed,
        anySelected,
        anyPending,
        filteredQnrs,

        // Setters
        setQuestionnaires,
        setActiveQnr,
        setSearchTerm,
        setQueryFilter,
        setCurrentPage,
        setSelectAll,
        setCrossWalkAnswers,
        setProposal,
        setSourceAnswer,

        // Functions
        refreshChatStatuses,
        fetchQuestionsForQnr,
        fetchCrosswalkData,
        handleFilterChange,
        handleSwitchQnr,
        setQuestions,
        updateQuestion,
        handleApprove,
        handleReject,
        handleUndo,
        handleUndoAll,
        handleApproveAll,
        handleRejectAll,
        handleApproveSelected,
        handleRejectSelected,
        toggleSelectAll,
        toggleSelect,
        handleOptionChange,
        handleCloseUploadPanel,
        handleConfidenceChange,
        handleSaveAnswer,
        saveReviewerNotes,
        // Two-way acceptance
        acceptanceStatus,
        reviewerAccepting,
        handleReviewerAccept,
    };

    return (
        <ReviewerContext.Provider value={value}>
            {children}
        </ReviewerContext.Provider>
    );
};

export default ReviewerContext;
